import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
	Loader2,
	ExternalLink,
	ChevronRight,
	ChevronDown,
} from 'lucide-react'
import { ClientDetailData, TimesheetEntry } from '../../../../hooks/useClientDetail'
import type { UseClientTimesheetProjectsResult } from '../../../../hooks/use-client-timesheet-projects'
import type { InvoiceRecord } from '../../../../utils/invoiceUtils'
import { aggregateInvoiceFinancialTotalsForSummary } from '../../../../utils/invoiceUtils'
import {
	getFilteredEffectiveRate,
	getPeriodTotalBilledFromInvoiceByPeriodSlices,
	getPeriodTotalBilledFromTrendSlices,
	getPeriodTotalBilledBaseFromInvoiceRecordsSlices,
	getPeriodTotalBilledUsdFromInvoiceRecordsSlices,
	type GoldFactSortColumn,
} from '../../../../utils/clientTimesheetProjectsUtils'
import { getRateForPeriod } from '../../../../utils/rateConfig'
import {
	ClientDetailTimesheetHoursOverviewCard,
	ClientDetailTimesheetRevenueMetricsCard,
} from './ClientDetailKpiCards'
import { appPath } from '../../../../utils/appPath'

/* ─── Props ──────────────────────────────────────────────────────────────── */

interface TimesheetProjectsTabProps {
	data: ClientDetailData
	ts: UseClientTimesheetProjectsResult
	onClearSharedPeriod: () => void
}

const PAGE_SIZE = 15
/** Chevron + 7 sort columns + Analysis */
const PROJECT_TABLE_COL_COUNT = 9

type ProjectListSortKey =
	| 'name'
	| 'billable'
	| 'total'
	| 'nonBillHrs'
	| 'estAmount'
	| 'expenses'
	| 'costNb'

interface ProjectListRow {
	pid: number
	name: string
	description: string
	isActive: boolean
	billable: number
	total: number
	nonBillHrs: number
	estAmount: number | null
	totalLaborCost: number | null
	costNb: number | null
}

/* ─── Embedded summary (legacy JSON only) ───────────────────────────────── */

function EmbeddedTimesheetSummary({ data }: { data: ClientDetailData }) {
	const rawEntries: TimesheetEntry[] =
		data?.timesheet_data || data?.project_timesheet || []
	const [displayCount, setDisplayCount] = useState(PAGE_SIZE)
	const [yearMonthFilter, setYearMonthFilter] = useState('')

	const availableYearMonths = useMemo(() => {
		const s = new Set<string>()
		rawEntries.forEach((e) => {
			if (e.year_month) s.add(String(e.year_month))
		})
		return Array.from(s).sort((a, b) => b.localeCompare(a))
	}, [rawEntries])

	const filtered = useMemo(() => {
		let entries = rawEntries
		if (yearMonthFilter) {
			entries = entries.filter((e) => e.year_month === yearMonthFilter)
		}
		return entries
	}, [rawEntries, yearMonthFilter])

	if (rawEntries.length === 0) return null

	const visible = filtered.slice(0, displayCount)

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-md p-4 space-y-3">
			<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
				Client file summary
			</h4>
			<p className="text-[11px] text-slate-500">
				Rows from enhanced client JSON (no project mapping). Use the project table above when available.
			</p>
			<div className="flex flex-wrap gap-2 items-center">
				<select
					value={yearMonthFilter}
					onChange={(e) => setYearMonthFilter(e.target.value)}
					className="px-2 py-1.5 text-[12px] border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400"
				>
					<option value="">All periods</option>
					{availableYearMonths.map((ym) => (
						<option key={ym} value={ym}>{ym}</option>
					))}
				</select>
				<span className="text-[11px] text-slate-600">{filtered.length} entries</span>
			</div>
			<div className="overflow-x-auto border border-slate-100 rounded-lg">
				<table className="w-full text-sm">
					<thead className="bg-slate-100 border-b border-slate-200">
						<tr>
							<th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase">Year-Month</th>
							<th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase">User</th>
							<th className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Total Hrs</th>
							<th className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Billable</th>
							<th className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Non-Billable</th>
							<th className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase">Entries</th>
						</tr>
					</thead>
					<tbody>
						{visible.map((entry, idx) => (
							<tr
								key={idx}
								className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/70' : ''}`}
							>
								<td className="px-3 py-2 text-slate-700 tabular-nums">{entry.year_month || '—'}</td>
								<td className="px-3 py-2 text-slate-600">{entry.user || '—'}</td>
								<td className="px-3 py-2 text-right tabular-nums">{entry.total_hours?.toFixed(1) ?? '—'}</td>
								<td className="px-3 py-2 text-right text-emerald-700 tabular-nums">{entry.billable_hours?.toFixed(1) ?? '—'}</td>
								<td className="px-3 py-2 text-right text-amber-600 tabular-nums">{entry.non_billable_hours?.toFixed(1) ?? '—'}</td>
								<td className="px-3 py-2 text-right text-slate-500 tabular-nums">{entry.entries ?? '—'}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
			{filtered.length > displayCount && (
				<button
					type="button"
					onClick={() => setDisplayCount((c) => c + PAGE_SIZE)}
					className="text-[12px] font-medium text-blue-600 hover:text-blue-800"
				>
					Load more
				</button>
			)}
		</div>
	)
}

/* ─── Component ──────────────────────────────────────────────────────────── */

export const TimesheetProjectsTab: React.FC<TimesheetProjectsTabProps> = ({
	data,
	ts,
	onClearSharedPeriod,
}) => {
	const invoiceRecords: InvoiceRecord[] = data?.detailed_invoices?.invoice_records || []

	const hasPeriodFilter =
		ts.filterCalYears.length > 0 ||
		ts.filterCalMonths.length > 0 ||
		ts.filterFYStarts.length > 0 ||
		ts.filterFYQuarters.length > 0
	const invoiceFinancialTotals = useMemo(
		() => aggregateInvoiceFinancialTotalsForSummary(invoiceRecords),
		[invoiceRecords],
	)

	const displayCurrency = useMemo(() => {
		const raw = String(
			data?.metadata?.original_currency ||
				data?.currency_analysis?.primary_currency ||
				'USD',
		)
			.trim()
			.toUpperCase()
		return /^[A-Z]{3}$/.test(raw) ? raw : 'USD'
	}, [data])

	const usdToDisplayCurrency = useMemo(() => {
		if (displayCurrency === 'USD') return 1
		const u = invoiceFinancialTotals.totalInvoicedUsd
		if (!(u > 0)) return 1
		return invoiceFinancialTotals.totalInvoicedBase / u
	}, [displayCurrency, invoiceFinancialTotals])

	const periodTotalBilledUsdFromRecords = getPeriodTotalBilledUsdFromInvoiceRecordsSlices(
		invoiceRecords,
		ts.filterCalYears,
		ts.filterCalMonths,
		ts.filterFYStarts,
		ts.filterFYQuarters,
	)
	const periodBilledBaseFromRecords = useMemo(
		() =>
			getPeriodTotalBilledBaseFromInvoiceRecordsSlices(
				invoiceRecords,
				ts.filterCalYears,
				ts.filterCalMonths,
				ts.filterFYStarts,
				ts.filterFYQuarters,
			),
		[
			invoiceRecords,
			ts.filterCalYears,
			ts.filterCalMonths,
			ts.filterFYStarts,
			ts.filterFYQuarters,
		],
	)
	const periodTotalBilledFromCibp = getPeriodTotalBilledFromInvoiceByPeriodSlices(
		ts.clientInvoiceByPeriod,
		ts.clientId,
		ts.filterCalYears,
		ts.filterCalMonths,
		ts.filterFYStarts,
		ts.filterFYQuarters,
	)
	const periodTotalBilledFromTrend = getPeriodTotalBilledFromTrendSlices(
		ts.clientRevenueTrendRows,
		ts.filterCalYears,
		ts.filterCalMonths,
		ts.filterFYStarts,
		ts.filterFYQuarters,
	)
	const periodTotalBilledLegacy =
		(periodTotalBilledFromCibp != null ? periodTotalBilledFromCibp : periodTotalBilledFromTrend) ?? null

	const allTimeBilledUsd =
		data?.financial_metrics?.total_invoiced_usd != null &&
		!Number.isNaN(parseFloat(String(data.financial_metrics.total_invoiced_usd)))
			? parseFloat(String(data.financial_metrics.total_invoiced_usd))
			: 0

	const totalBilledUsd = useMemo(() => {
		if (hasPeriodFilter) {
			if (periodTotalBilledUsdFromRecords != null) {
				return periodTotalBilledUsdFromRecords
			}
			return periodTotalBilledLegacy ?? 0
		}
		return allTimeBilledUsd
	}, [
		hasPeriodFilter,
		periodTotalBilledUsdFromRecords,
		periodTotalBilledLegacy,
		allTimeBilledUsd,
	])

	const totalBilledForDisplay = useMemo(() => {
		if (displayCurrency === 'USD') return totalBilledUsd
		if (
			hasPeriodFilter &&
			periodTotalBilledUsdFromRecords != null &&
			periodBilledBaseFromRecords != null
		) {
			return periodBilledBaseFromRecords
		}
		if (hasPeriodFilter) {
			return totalBilledUsd * usdToDisplayCurrency
		}
		if (invoiceFinancialTotals.totalInvoicedBase > 0) {
			return invoiceFinancialTotals.totalInvoicedBase
		}
		return totalBilledUsd * usdToDisplayCurrency
	}, [
		displayCurrency,
		hasPeriodFilter,
		periodTotalBilledUsdFromRecords,
		periodBilledBaseFromRecords,
		totalBilledUsd,
		usdToDisplayCurrency,
		invoiceFinancialTotals.totalInvoicedBase,
	])

	const hasBilled =
		(Number.isFinite(totalBilledUsd) && totalBilledUsd > 0) ||
		(Number.isFinite(totalBilledForDisplay) && totalBilledForDisplay > 0)

	const displayMoneyScale = displayCurrency === 'USD' ? 1 : usdToDisplayCurrency

	const formatProjectMoney0 = useCallback(
		(n: number) =>
			new Intl.NumberFormat(undefined, {
				style: 'currency',
				currency: displayCurrency,
				maximumFractionDigits: 0,
			}).format(n),
		[displayCurrency],
	)

	const formatProjectPerHour = useCallback(
		(n: number) =>
			`${new Intl.NumberFormat(undefined, {
				style: 'currency',
				currency: displayCurrency,
				maximumFractionDigits: 2,
			}).format(n)}/hr`,
		[displayCurrency],
	)

	const projectStatusCounts = useMemo(() => {
		const active = ts.projects.filter((p) => p.is_active === 1).length
		const inactive = ts.projects.filter((p) => p.is_active === 0).length
		return { active, inactive }
	}, [ts.projects])

	const [projectListSortKey, setProjectListSortKey] =
		useState<ProjectListSortKey>('name')
	const [projectListSortDir, setProjectListSortDir] = useState<'asc' | 'desc'>('asc')

	const projectTableRows: ProjectListRow[] = useMemo(() => {
		if (!ts.projects.length) return []
		const dateEnd = ts.consolidatedStats?.dateRange?.end
		const endY = dateEnd ? parseInt(dateEnd.substring(0, 4), 10) : null
		const endM = dateEnd ? parseInt(dateEnd.substring(5, 7), 10) : null
		const defaultRate = getRateForPeriod(ts.rateConfig, endY, endM)
		const totalBillable = ts.consolidatedStats?.billableHours || 0
		return ts.projects.map((project) => {
			const pid = project.project_id
			const projStats = ts.consolidatedStats?.projectStatsMap?.[pid]
			const projBillable = projStats?.billableHours ?? 0
			const projNonBillable = projStats?.nonBillableHours ?? 0
			const projTotal = projStats?.totalHours ?? 0
			const totalLaborCostUsd =
				projStats?.totalExpenses != null
					? projStats.totalExpenses
					: projBillable > 0
						? projBillable * defaultRate
						: null
			const costNbUsd =
				projStats?.costOfNonBillable != null
					? projStats.costOfNonBillable
					: projNonBillable > 0
						? projNonBillable * defaultRate
						: null
			const totalLaborCost =
				totalLaborCostUsd != null
					? totalLaborCostUsd * displayMoneyScale
					: null
			const costNb =
				costNbUsd != null ? costNbUsd * displayMoneyScale : null
			const estAmount =
				hasBilled && totalBillable > 0 && projBillable >= 0
					? (projBillable / totalBillable) * totalBilledForDisplay
					: null
			return {
				pid,
				name: project.project_name || `Project ${pid}`,
				description: project.project_description
					? String(project.project_description)
					: '',
				isActive: project.is_active === 1,
				billable: projBillable,
				total: projTotal,
				nonBillHrs: projNonBillable,
				estAmount,
				totalLaborCost,
				costNb,
			}
		})
	}, [
		ts.projects,
		ts.consolidatedStats,
		ts.rateConfig,
		hasBilled,
		totalBilledForDisplay,
		displayMoneyScale,
	])

	const sortedProjectTableRows = useMemo(() => {
		const rows = [...projectTableRows]
		const d = projectListSortDir === 'asc' ? 1 : -1
		const m = projectListSortKey
		rows.sort((a, b) => {
			let c = 0
			switch (m) {
				case 'name':
					c = a.name.localeCompare(b.name)
					break
				case 'billable':
					c = a.billable - b.billable
					break
				case 'total':
					c = a.total - b.total
					break
				case 'nonBillHrs':
					c = a.nonBillHrs - b.nonBillHrs
					break
				case 'estAmount':
					c = (a.estAmount ?? -Infinity) - (b.estAmount ?? -Infinity)
					break
				case 'expenses':
					c =
						(a.totalLaborCost ?? -Infinity) -
						(b.totalLaborCost ?? -Infinity)
					break
				case 'costNb':
					c = (a.costNb ?? -Infinity) - (b.costNb ?? -Infinity)
					break
				default:
					break
			}
			return c * d
		})
		return rows
	}, [projectTableRows, projectListSortKey, projectListSortDir])

	const handleProjectListSort = useCallback(
		(key: ProjectListSortKey) => {
			if (projectListSortKey === key) {
				setProjectListSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
			} else {
				setProjectListSortKey(key)
				setProjectListSortDir(key === 'name' ? 'asc' : 'desc')
			}
		},
		[projectListSortKey],
	)

	const ThSort: React.FC<{
		field: GoldFactSortColumn
		label: string
		className?: string
		align?: 'left' | 'right'
	}> = ({ field, label, className = '', align = 'left' }) => {
		const active = ts.sortColumn === field
		return (
			<th
				className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer select-none hover:text-slate-700 whitespace-nowrap ${
					align === 'right' ? 'text-right' : 'text-left'
				} ${className}`}
				onClick={() => ts.handleSort(field)}
			>
				{label}
				<span className={`ml-1 ${active ? 'text-blue-500' : 'text-slate-300'}`}>
					{active ? (ts.sortDirection === 'asc' ? '↑' : '↓') : '↕'}
				</span>
			</th>
		)
	}

	const ProjectListTh: React.FC<{
		sortKey: ProjectListSortKey
		label: string
		align?: 'left' | 'right'
	}> = ({ sortKey: k, label, align = 'left' }) => {
		const active = projectListSortKey === k
		return (
			<th
				className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer select-none hover:text-slate-700 whitespace-nowrap ${
					align === 'right' ? 'text-right' : 'text-left'
				}`}
				onClick={() => handleProjectListSort(k)}
			>
				{label}
				<span className={`ml-1 ${active ? 'text-blue-500' : 'text-slate-300'}`}>
					{active ? (projectListSortDir === 'asc' ? '↑' : '↓') : '↕'}
				</span>
			</th>
		)
	}

	const embeddedRows =
		(data?.timesheet_data || data?.project_timesheet || []) as TimesheetEntry[]

	if (ts.loading) {
		return (
			<div className="bg-white rounded-xl border border-slate-200 shadow-md p-12 flex flex-col items-center justify-center gap-3">
				<Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
				<p className="text-slate-600 text-sm">Loading timesheet projects…</p>
			</div>
		)
	}

	if (ts.error && ts.projects.length === 0) {
		return (
			<div className="space-y-3">
				<div className="bg-white rounded-xl border border-red-200 shadow-md p-8 text-center">
					<p className="text-red-600 font-medium text-sm mb-1">Could not load project mapping</p>
					<p className="text-slate-600 text-xs">{ts.error}</p>
				</div>
				<EmbeddedTimesheetSummary data={data} />
				{embeddedRows.length === 0 && (
					<p className="text-center text-slate-500 text-sm">No timesheet rows in client file.</p>
				)}
			</div>
		)
	}

	if (ts.projects.length === 0) {
		return (
			<div className="space-y-3">
				<div className="bg-white rounded-xl border border-slate-200 shadow-md p-12 text-center">
					<p className="text-slate-600 text-sm">No timesheet projects mapped to this client.</p>
					<p className="text-slate-500 text-xs mt-2">
						Check client–project mapping under public/ts_project_json.
					</p>
				</div>
				<EmbeddedTimesheetSummary data={data} />
			</div>
		)
	}

	const dateEnd = ts.consolidatedStats?.dateRange?.end
	const endYear = dateEnd ? parseInt(dateEnd.substring(0, 4), 10) : null
	const endMonth = dateEnd ? parseInt(dateEnd.substring(5, 7), 10) : null

	return (
		<div className="space-y-3">
			{/* Hours overview (Invoice-style KPI shell) + remaining metrics */}
			{ts.consolidatedStats && (
				<div className="space-y-3">
					{(() => {
						const billedValue = totalBilledForDisplay
						const billableH = ts.consolidatedStats.billableHours || 0
						const nonBillableH = ts.consolidatedStats.nonBillableHours || 0
						const totalH = ts.consolidatedStats.totalHours || 0
						const util = ts.consolidatedStats.utilizationRate || 0
						const effectiveRateUsd = getFilteredEffectiveRate(
							data,
							totalBilledUsd,
							billableH,
						)
						const effectiveRate =
							effectiveRateUsd != null
								? effectiveRateUsd * (displayCurrency === 'USD' ? 1 : usdToDisplayCurrency)
								: null
						const totalLaborCostUsd =
							ts.consolidatedStats.totalExpenses != null
								? ts.consolidatedStats.totalExpenses
								: billableH > 0
									? billableH * getRateForPeriod(ts.rateConfig, endYear, endMonth)
									: null
						const costOfNonBillableUsd =
							ts.consolidatedStats.costOfNonBillable != null
								? ts.consolidatedStats.costOfNonBillable
								: nonBillableH > 0
									? nonBillableH * getRateForPeriod(ts.rateConfig, endYear, endMonth)
									: null
						const totalLaborCost =
							totalLaborCostUsd != null
								? totalLaborCostUsd * displayMoneyScale
								: null
						const costOfNonBillable =
							costOfNonBillableUsd != null
								? costOfNonBillableUsd * displayMoneyScale
								: null
						const profit =
							hasBilled && totalLaborCost != null
								? billedValue - totalLaborCost
								: null
						const avgCostPerHour =
							billableH > 0 && totalLaborCost != null
								? totalLaborCost / billableH
								: null
						const marginPerBillableHour =
							effectiveRate != null &&
							avgCostPerHour != null &&
							effectiveRate > avgCostPerHour
								? effectiveRate - avgCostPerHour
								: null
						const billedHint =
							displayCurrency === 'USD'
								? 'USD. With period: from invoices (billed month) when possible.'
								: `Amounts in ${displayCurrency} from invoice face values when available; period fallbacks use totalInvoicedBase ÷ USD from all invoices.`

						return (
							<div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
								<ClientDetailTimesheetHoursOverviewCard
									compact
									totalHrs={totalH}
									billableHrs={billableH}
									nonBillableHrs={nonBillableH}
									utilizationPct={util}
									title="Period hours"
									totalHrsHint="Sum of hours in the selected period across all mapped projects."
								/>
								<ClientDetailTimesheetRevenueMetricsCard
									hasBilled={hasBilled}
									billedValue={billedValue}
									effectiveRate={effectiveRate}
									totalLaborCost={totalLaborCost}
									costOfNonBillable={costOfNonBillable}
									profit={profit}
									marginPerBillableHour={marginPerBillableHour}
									title={`Revenue & margin (${displayCurrency})`}
									billedHint={billedHint}
									effectiveRateHint="Total billed ÷ billable hours (or line-item blend), shown in client currency."
									expensesHint="Internal cost model in USD, converted at same blend rate as billed totals."
									formatMoney0={formatProjectMoney0}
									formatPerHour={formatProjectPerHour}
								/>
							</div>
						)
					})()}
				</div>
			)}

			{/* Project list */}
			<div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
				<div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
					<h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
						Projects ({ts.projects.length})
					</h4>
					<div className="flex items-center gap-3 sm:gap-4">
						{ts.loadingConsolidated && (
							<span className="inline-flex items-center gap-1.5 text-[10px] text-slate-500 whitespace-nowrap">
								<Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" aria-hidden />
								Loading…
							</span>
						)}
						<div
							className="flex items-center gap-4 flex-wrap"
							aria-label={`${projectStatusCounts.active} active, ${projectStatusCounts.inactive} inactive projects`}
						>
							<span className="inline-flex items-center gap-1.5" title="is_active = 1">
								<span
									className="h-2 w-2 rounded-full shrink-0 bg-emerald-500 ring-1 ring-black/5"
									aria-hidden
								/>
								<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
									Active
								</span>
								<span className="text-[11px] font-semibold tabular-nums text-slate-800">
									{projectStatusCounts.active}
								</span>
							</span>
							<span className="inline-flex items-center gap-1.5" title="is_active = 0">
								<span
									className="h-2 w-2 rounded-full shrink-0 bg-slate-300 ring-1 ring-black/5"
									aria-hidden
								/>
								<span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
									Inactive
								</span>
								<span className="text-[11px] font-semibold tabular-nums text-slate-800">
									{projectStatusCounts.inactive}
								</span>
							</span>
						</div>
					</div>
				</div>
				<div className="overflow-x-auto">
					<table className="w-full text-sm min-w-[640px]">
						<thead className="bg-slate-100 border-b-2 border-slate-200">
							<tr>
								<th
									className="w-9 px-2 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400"
									aria-hidden
								>
									{' '}
								</th>
								<ProjectListTh sortKey="name" label="Project" />
								<ProjectListTh sortKey="billable" label="Billable" align="right" />
								<ProjectListTh sortKey="total" label="Total hrs" align="right" />
								<ProjectListTh sortKey="nonBillHrs" label="Non-bill" align="right" />
								<ProjectListTh sortKey="estAmount" label={`Est. billed (${displayCurrency})`} align="right" />
								<ProjectListTh sortKey="expenses" label={`Expenses (${displayCurrency})`} align="right" />
								<ProjectListTh sortKey="costNb" label={`Non-bill (${displayCurrency})`} align="right" />
								<th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">
									Analysis
								</th>
							</tr>
						</thead>
						<tbody>
							{sortedProjectTableRows.map((row, idx) => {
								const selected = ts.selectedProjectId === row.pid
								const showDetailRow =
									selected &&
									(ts.loadingProjectData ||
										ts.projectData != null ||
										!!ts.projectDataError)
								const toggleRow = () => {
									if (selected) ts.closeProjectDetail()
									else void ts.selectProject(row.pid)
								}
								return (
									<React.Fragment key={row.pid}>
										<tr
											role="button"
											tabIndex={0}
											aria-expanded={selected}
											onClick={toggleRow}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault()
													toggleRow()
												}
											}}
											className={[
												'border-t border-slate-100 cursor-pointer transition-colors',
												idx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white',
												selected
													? 'bg-blue-50/90 ring-1 ring-inset ring-blue-200'
													: 'hover:bg-blue-50/40',
											].join(' ')}
										>
											<td className="w-9 px-2 py-2.5 align-middle">
												{selected ? (
													<ChevronDown
														className="w-4 h-4 text-slate-600 shrink-0"
														aria-hidden
													/>
												) : (
													<ChevronRight
														className="w-4 h-4 text-slate-400 shrink-0"
														aria-hidden
													/>
												)}
											</td>
											<td className="px-3 py-2.5 max-w-[18rem]">
												<div className="flex items-start gap-2 min-w-0">
													<span className="sr-only">
														{row.isActive ? 'Active' : 'Inactive'}
													</span>
													<span
														className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ring-1 ring-black/5 ${
															row.isActive ? 'bg-emerald-500' : 'bg-slate-300'
														}`}
														title={row.isActive ? 'Active' : 'Inactive'}
														aria-hidden
													/>
													<span
														className="font-medium text-slate-800 line-clamp-2 min-w-0"
														title={
															row.description
																? `${row.name}\n${row.description}`
																: row.name
														}
													>
														{row.name}
													</span>
												</div>
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-emerald-800 font-medium">
												{row.billable.toLocaleString(undefined, { maximumFractionDigits: 1 })}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-slate-800">
												{row.total.toLocaleString(undefined, { maximumFractionDigits: 1 })}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-amber-700">
												{row.nonBillHrs.toLocaleString(undefined, { maximumFractionDigits: 1 })}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-emerald-700 font-medium">
												{row.estAmount != null
													? formatProjectMoney0(row.estAmount)
													: '—'}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
												{row.totalLaborCost != null
													? formatProjectMoney0(row.totalLaborCost)
													: '—'}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-amber-800">
												{row.costNb != null
													? formatProjectMoney0(row.costNb)
													: '—'}
											</td>
											<td className="px-3 py-2.5 text-right whitespace-nowrap">
												<Link
													to={appPath(`/project-analysis/${row.pid}`)}
													onClick={(e) => e.stopPropagation()}
													className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800"
												>
													<ExternalLink className="w-3 h-3 shrink-0" />
													Open
												</Link>
											</td>
										</tr>
										{showDetailRow && (
											<tr className="border-t border-slate-200 bg-slate-50/40">
												<td
													colSpan={PROJECT_TABLE_COL_COUNT}
													className="p-0 align-top"
												>
													<div className="px-4 py-2 pb-3 space-y-2">
														{ts.loadingProjectData &&
															!ts.projectData &&
															!ts.projectDataError && (
															<div className="flex items-center gap-2 text-slate-500 text-sm py-4 justify-center">
																<Loader2 className="w-5 h-5 animate-spin shrink-0" />
																Loading…
															</div>
														)}

														{ts.projectDataError && (
															<div className="rounded-lg border border-red-200 bg-red-50/50 px-3 py-2 text-[12px] text-red-700">
																{ts.projectDataError}
															</div>
														)}

														{!ts.projectDataError &&
															ts.projectData &&
															ts.projectData.length > 0 && (
															<div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
																<div className="overflow-x-auto">
																	<table className="w-full text-sm">
																		<thead className="bg-slate-100 border-b border-slate-200">
																			<tr>
																				<ThSort field="year-month" label="Year-Month" />
																				<ThSort field="user" label="User" />
																				<ThSort field="total-hours" label="Total Hrs" align="right" />
																				<ThSort field="billable" label="Billable" align="right" />
																				<ThSort field="non-billable" label="Non-Bill" align="right" />
																				<ThSort field="entries" label="Entries" align="right" />
																			</tr>
																		</thead>
																		<tbody>
																			{ts.sortedDetailRows
																				.slice(0, ts.displayCount)
																				.map((entry, index) => (
																					<tr
																						key={`${entry.user_id}-${entry.year}-${entry.month}-${index}`}
																						className={`border-t border-slate-100 ${index % 2 === 1 ? 'bg-slate-50/50' : ''}`}
																					>
																						<td className="px-3 py-2 tabular-nums text-slate-800">
																							{entry.year}-{String(entry.month).padStart(2, '0')}
																						</td>
																						<td className="px-3 py-2 text-slate-600">
																							{entry.full_name || entry.username || '—'}
																						</td>
																						<td className="px-3 py-2 text-right tabular-nums">
																							{parseFloat(String(entry.total_hours || 0)).toFixed(2)}
																						</td>
																						<td className="px-3 py-2 text-right tabular-nums text-emerald-800">
																							{parseFloat(String(entry.billable_hours || 0)).toFixed(2)}
																						</td>
																						<td className="px-3 py-2 text-right tabular-nums text-amber-700">
																							{parseFloat(String(entry.non_billable_hours || 0)).toFixed(2)}
																						</td>
																						<td className="px-3 py-2 text-right tabular-nums text-slate-600">
																							{entry.entries_count ?? 0}
																						</td>
																					</tr>
																				))}
																		</tbody>
																	</table>
																</div>
																{ts.sortedDetailRows.length > ts.displayCount && (
																	<div className="px-3 py-2 border-t border-slate-100 text-center bg-white">
																		<button
																			type="button"
																			onClick={ts.loadMoreDetail}
																			className="text-[12px] font-medium text-blue-600 hover:text-blue-800"
																		>
																			Load more ({ts.sortedDetailRows.length - ts.displayCount} left)
																		</button>
																	</div>
																)}
																{ts.sortedDetailRows.length === 0 &&
																	ts.projectData.length > 0 && (
																	<div className="p-6 text-center text-sm text-slate-500 bg-white border-t border-slate-100">
																		No rows match filters.{' '}
																		<button
																			type="button"
																			className="text-blue-600 font-medium"
																			onClick={() => {
																				onClearSharedPeriod()
																				ts.setDisplayCount(20)
																			}}
																		>
																			Clear filters
																		</button>
																	</div>
																)}
															</div>
														)}

														{!ts.loadingProjectData &&
															!ts.projectDataError &&
															ts.projectData &&
															ts.projectData.length === 0 && (
															<p className="text-center text-sm text-slate-500 py-4">
																No timesheet rows for this project.
															</p>
														)}
													</div>
												</td>
											</tr>
										)}
									</React.Fragment>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>

		</div>
	)
}

export default TimesheetProjectsTab
